import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Save, X, CircleMinus, CircleCheck, Lock } from "lucide-react";
import FormControl from "../../common/FormControl";
import Modal from "../../common/Modal";
import Button from "../../common/Button";
import Skeleton from "../../common/Skeleton";
import NoDataFound from "../../common/NoDataFound";
import Toast, { type ToastMessage } from "../../common/Toast";
import ApprovalDot, { WorkflowState } from "../../common/ApprovalDot";
import WorkflowModal from "../../common/WorkflowModal";
import StatusSwitch from "../../common/StatusSwitch";
import Pagination from "../../common/Pagination";
import { usePermission, NOT_AUTHORISED_MSG } from "../../../contexts/PermissionContext";
import { listReportingMap, assignManager, deleteReportingMap, setReportingMapActive } from "../../../services/hrService";
import { userService } from "../../../services/userService";

const RBAC_ACTIVITY = "HR_REPORTING_MAP";

interface MapRow {
    staff_user_id: number;
    manager_user_id?: number | null;
    mapping_id?: number | null;
    self_approving?: boolean;
    workflow_state?: string | null;
    is_active?: number | null;
}
interface UserOpt { id: number; name: string; email: string; }

const ReportingMapAdmin: React.FC = () => {
    const modifyPerm = usePermission(RBAC_ACTIVITY, "view+modify");
    const canModify = modifyPerm.allowed;

    const [rows, setRows] = useState<MapRow[]>([]);
    const [users, setUsers] = useState<UserOpt[]>([]);
    const [statusFilter, setStatusFilter] = useState<"ACTIVE" | "INACTIVE">("ACTIVE");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const [target, setTarget] = useState<MapRow | null>(null);
    const [workflowRow, setWorkflowRow] = useState<MapRow | null>(null);
    const [managerId, setManagerId] = useState(0);
    const [saving, setSaving] = useState(false);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [raw, u] = await Promise.all([listReportingMap(), userService.getUsersDropdown()]);
            // The endpoint returns { items, total }; tolerate either shape.
            const list: MapRow[] = Array.isArray(raw) ? (raw as any) : ((raw as any)?.items || []);
            setRows(list);
            setUsers(u);
        } catch (err: any) {
            setToast({ type: "error", message: err.response?.data?.detail || err.message || "Failed to load" });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);
    useEffect(() => { setPage(1); }, [statusFilter, pageSize]);

    const filtered = useMemo(() =>
        rows.filter((r) => statusFilter === "ACTIVE" ? !!r.mapping_id : !r.mapping_id),
        [rows, statusFilter]
    );
    const paginatedRows = useMemo(
        () => filtered.slice((page - 1) * pageSize, page * pageSize),
        [filtered, page, pageSize]
    );

    const userName = (id?: number | null) => (id ? users.find((u) => u.id === id)?.name || `User #${id}` : "-");

    const openAssign = (r: MapRow) => {
        setTarget(r);
        setManagerId(r.manager_user_id || 0);
    };

    const handleAssign = async () => {
        if (!target) return;
        if (!managerId) { setToast({ type: "error", message: "Select a manager." }); return; }
        if (managerId === target.staff_user_id) { setToast({ type: "error", message: "A person cannot report to themselves." }); return; }
        setSaving(true);
        try {
            await assignManager({ staff_user_id: target.staff_user_id, manager_user_id: managerId });
            setToast({ type: "success", message: "Manager assigned" });
            setTarget(null);
            fetchAll();
        } catch (err: any) {
            setToast({ type: "error", message: err.response?.data?.detail || err.message || "Assign failed" });
        } finally {
            setSaving(false);
        }
    };

    // Active/Inactive lifecycle toggle (is_active 1↔0) — inactive ⇒ self-approving.
    const handleToggleActive = async (next: boolean) => {
        if (!target?.mapping_id) return;
        setSaving(true);
        try {
            await setReportingMapActive(target.mapping_id, next);
            setToast({ type: next ? "success" : "warning", message: next ? "Mapping reactivated." : "Mapping inactivated — staff is now self-approving." });
            setTarget(null);
            fetchAll();
        } catch (err: any) {
            setToast({ type: "error", message: err.response?.data?.detail || err.message || "Failed to update status." });
        } finally {
            setSaving(false);
        }
    };

    const handleClear = async (r: MapRow) => {
        if (!r.mapping_id) return;
        setSaving(true);
        try {
            await deleteReportingMap(r.mapping_id);
            setToast({ type: "success", message: "Mapping cleared — now self-approving" });
            setTarget(null);
            fetchAll();
        } catch (err: any) {
            setToast({ type: "error", message: err.response?.data?.detail || err.message || "Clear failed" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="netbody-card flex flex-col h-full">
            <div className="netbody-header">
                <StatusSwitch
                    value={statusFilter}
                    onChange={(next) => setStatusFilter(next)}
                    noun="mappings"
                    testId="SEL_reporting_status"
                />
                <div className="ml-auto" />
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                <div className="table-list">
                    <div className="table-list-scroll">
                    <table className="table-list-table">
                        <thead>
                            <tr>
                                <th className="table-list-th" style={{ width: 34 }} aria-label="Workflow" />
                                <th className="table-list-th table-list-cell--sno">S.No</th>
                                <th className="table-list-th">Code</th>
                                <th className="table-list-th">Staff</th>
                                <th className="table-list-th">Reports To</th>
                                <th className="table-list-th">Status</th>
                            </tr>
                        </thead>
                        <tbody aria-busy={loading} aria-live="polite">
                            {loading && <Skeleton.TableRows cols={["pill", "3xs", "2xs", "md", "md", "pill"]} />}
                            {!loading && filtered.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="table-list-cell">
                                        <NoDataFound
                                            title={statusFilter === "INACTIVE" ? "No self-approving members" : "No mapped members"}
                                            message={statusFilter === "INACTIVE" ? "All members have managers assigned." : "No manager mappings exist yet."}
                                        />
                                    </td>
                                </tr>
                            )}
                            {!loading && paginatedRows.map((r, idx) => (
                                    <tr
                                        key={r.staff_user_id}
                                        className="table-list-row"
                                        data-testid={`ROW_reporting_${r.staff_user_id}`}
                                        onClick={() => canModify && openAssign(r)}
                                        style={{ cursor: canModify ? "pointer" : "default" }}
                                    >
                                        <td className="table-list-cell" style={{ width: 34 }} onClick={r.mapping_id ? (e) => { e.stopPropagation(); setWorkflowRow(r); } : undefined} title={r.mapping_id ? "Approval workflow & history" : undefined} data-testid={`DOT_reporting_status_${r.staff_user_id}`}>
                                            {r.mapping_id
                                                ? <ApprovalDot
                                                    state={(r.workflow_state || "draft") as WorkflowState}
                                                    onClick={(e) => { e.stopPropagation(); setWorkflowRow(r); }}
                                                    title="Approval workflow & history"
                                                />
                                                : <ApprovalDot state="draft" title="Self-approving — no mapping" />}
                                        </td>
                                        <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                        <td className="table-list-cell">{(r as any).code || "—"}</td>
                                        <td className="table-list-cell"><span className="table-list-cell-title cell-name-lock">{r.workflow_state === 'approved' && (<Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />)}{userName(r.staff_user_id)}</span></td>
                                        <td className="table-list-cell">
                                            {r.self_approving || !r.manager_user_id
                                                ? <span className="status-badge xs warning" data-testid={`BADGE_self_approving_${r.staff_user_id}`}>Self-approving</span>
                                                : userName(r.manager_user_id)}
                                        </td>
                                        <td className="table-list-cell">
                                            <span className={`status-badge xs ${r.mapping_id ? "success" : "neutral"}`}>
                                                {r.mapping_id ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                    </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        totalRecords={filtered.length}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                        pageSizeOptions={[10, 25, 50, 100]}
                        recordLabel="mappings"
                    />
                </div>
                </div>
            </div>

            <Modal isOpen={!!target} onClose={() => setTarget(null)} size="sm" data-testid="MOD_reporting">
                <Modal.Header>Assign Manager</Modal.Header>
                <Modal.Body>
                    <p style={{ fontSize: "0.8125rem", color: "var(--text-secondary)", marginBottom: 12 }}>
                        Staff: <strong>{target && userName(target.staff_user_id)}</strong>
                    </p>
                    <FormControl.SearchSelect
                        label="Manager"
                        required
                        value={managerId || null}
                        onChange={(v) => setManagerId(v ? Number(v) : 0)}
                        placeholder="Select manager..."
                        options={users
                            .filter((u) => u.id !== target?.staff_user_id)
                            .map((u) => ({ value: u.id, label: `${u.name}${u.email ? ` (${u.email})` : ""}` }))}
                        data-testid="SEL_reporting_manager"
                    />
                </Modal.Body>
                <Modal.Footer className={canModify ? "app-modal-footer--split" : ""}>
                    {/* Active/Inactive — always visible like Holiday Details; disabled when no mapping exists yet. */}
                    {canModify && (
                        <Button
                            variant={!!target?.mapping_id && (target?.is_active ?? 1) !== 0 ? "inactive-outline" : "active-outline"}
                            icon={!!target?.mapping_id && (target?.is_active ?? 1) !== 0 ? CircleMinus : CircleCheck}
                            onClick={() => handleToggleActive(!!target?.mapping_id && (target?.is_active ?? 1) === 0)}
                            disabled={saving || !target?.mapping_id}
                            title={!target?.mapping_id ? "Assign a manager first to enable this toggle" : undefined}
                            data-testid="TGL_reporting_active"
                        >
                            {!!target?.mapping_id && (target?.is_active ?? 1) !== 0 ? "Inactive" : "Active"}
                        </Button>
                    )}
                    {canModify && target?.mapping_id && (
                        <Button variant="delete-outline" icon={X} onClick={() => target && handleClear(target)} disabled={saving} data-testid="BTN_reporting_clear">
                            Make Self-approving
                        </Button>
                    )}
                    <Button variant="fill" icon={Save} onClick={handleAssign} disabled={!canModify || !managerId || saving} title={!canModify ? NOT_AUTHORISED_MSG : undefined} data-testid="BTN_reporting_save">
                        Assign
                    </Button>
                </Modal.Footer>
            </Modal>

            <WorkflowModal
                isOpen={!!workflowRow}
                onClose={() => setWorkflowRow(null)}
                kind="hr_reporting_map"
                id={workflowRow?.mapping_id ?? null}
                onChanged={fetchAll}
            />

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default ReportingMapAdmin;
